import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import {
  COUPONS, COUPONS_COLLECTION, CODE_RE, normalizeCoupon, couponState, couponHeadline, couponTerms,
} from '../Data/offers';

// Coupons: every code the website and checkout offer. A code saved here is
// live at once - checkout reads it, the customer's account shows it, and the
// server (api/place-order.js) checks each order against the same rules
// (Data/offers.js, shared with the website). Uses are counted by the server.

const STATE = {
  running: { label: 'Running', color: '#187044', bg: '#e8f4ec' },
  scheduled: { label: 'Scheduled', color: '#1d4f91', bg: '#e9f0fa' },
  ended: { label: 'Ended', color: '#7a6750', bg: '#f3eee6' },
  used_up: { label: 'Used up', color: '#7a6750', bg: '#f3eee6' },
  off: { label: 'Off', color: '#9b2c2c', bg: '#fdecea' },
};
const FILTERS = [['all', 'All'], ['running', 'Running'], ['scheduled', 'Scheduled'], ['done', 'Ended / used up'], ['off', 'Off']];

const toDateInput = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fromDateInput = (v, endOfDay) => {
  if (!v) return null;
  const [y, m, d] = v.split('-').map(Number);
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59) : new Date(y, m - 1, d, 0, 0, 0);
};
const when = (v) => {
  const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';
};

const blank = () => ({
  code: '', type: 'flat', value: '', maxDiscount: '', minOrder: '', startsAt: toDateInput(Date.now()), endsAt: '',
  firstOrderOnly: false, perCustomer: '', totalLimit: '', active: true, headline: '', note: '',
});
const formFrom = (c) => ({
  code: c.code, type: c.type, value: String(c.value || ''), maxDiscount: c.maxDiscount ? String(c.maxDiscount) : '',
  minOrder: c.minOrder ? String(c.minOrder) : '', startsAt: toDateInput(c.startsAt), endsAt: toDateInput(c.endsAt),
  firstOrderOnly: c.firstOrderOnly, perCustomer: c.perCustomer ? String(c.perCustomer) : '', totalLimit: c.totalLimit ? String(c.totalLimit) : '',
  active: c.active, headline: c.headline, note: c.note,
});
// The document saved for a form.
const docFrom = (f) => ({
  code: f.code.trim().toUpperCase(),
  type: f.type,
  value: Number(f.value) || 0,
  maxDiscount: f.type === 'percent' ? Number(f.maxDiscount) || 0 : 0,
  minOrder: Number(f.minOrder) || 0,
  startsAt: fromDateInput(f.startsAt, false),
  endsAt: fromDateInput(f.endsAt, true),
  firstOrderOnly: !!f.firstOrderOnly,
  perCustomer: Math.floor(Number(f.perCustomer) || 0),
  totalLimit: Math.floor(Number(f.totalLimit) || 0),
  active: !!f.active,
  headline: f.headline.trim(),
  note: f.note.trim(),
});
const problemsIn = (f, isNew, taken) => {
  const p = {};
  const code = f.code.trim().toUpperCase();
  if (!CODE_RE.test(code)) p.code = '3 to 20 capital letters or digits, no spaces.';
  else if (isNew && taken.has(code)) p.code = 'A coupon with this code already exists.';
  const v = Number(f.value);
  if (!(v > 0)) p.value = 'Enter how much it takes off.';
  else if (f.type === 'percent' && v > 90) p.value = 'Up to 90%.';
  if (f.type === 'percent' && f.maxDiscount !== '' && !(Number(f.maxDiscount) > 0)) p.maxDiscount = 'Leave empty for no cap, or enter an amount.';
  if (f.minOrder !== '' && Number(f.minOrder) < 0) p.minOrder = 'Cannot be negative.';
  if (f.type === 'flat' && Number(f.minOrder) && v >= Number(f.minOrder)) p.value = 'The discount should be less than the minimum order.';
  if (f.startsAt && f.endsAt && fromDateInput(f.endsAt, true) < fromDateInput(f.startsAt, false)) p.endsAt = 'Ends before it starts.';
  return p;
};

const card = { background: '#fff', border: '1px solid #e2d7c6', borderRadius: 4, padding: 16 };
const label = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.78rem', fontWeight: 700, color: '#4a4034' };
const input = { height: 38, padding: '0 10px', border: '1px solid #d7cbb7', borderRadius: 3, fontSize: '0.9rem', background: '#fff' };
const hint = { fontSize: '0.72rem', fontWeight: 500, color: '#7a6750' };
const err = { fontSize: '0.72rem', fontWeight: 600, color: '#b33b2f' };
const btn = (dark) => ({
  height: 36, padding: '0 14px', borderRadius: 3, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
  border: dark ? '1px solid #3e4a2f' : '1px solid #d7cbb7', background: dark ? '#3e4a2f' : '#fff', color: dark ? '#fff' : '#3e4a2f',
});

function Field({ title, note, error, children }) {
  return (
    <label style={label}>
      <span>{title}</span>
      {children}
      {error ? <span style={err}>{error}</span> : note ? <span style={hint}>{note}</span> : null}
    </label>
  );
}

function CouponForm({ initial, isNew, taken, onCancel, onSave }) {
  const [f, setF] = useState(initial);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const problems = problemsIn(f, isNew, taken);
  const show = (k) => (tried ? problems[k] : '');
  const preview = normalizeCoupon(docFrom({ ...f, code: f.code || 'CODE' }));

  const save = async () => {
    setTried(true);
    if (Object.keys(problems).length) return;
    setBusy(true);
    try { await onSave(docFrom(f)); } finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, borderColor: '#3e4a2f', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{isNew ? 'New coupon' : `Edit ${initial.code}`}</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.85rem' }}>
          <input type="checkbox" checked={f.active} onChange={set('active')} /> Active
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
        <Field title="Code" note={isNew ? 'What the customer types, e.g. DIWALI500.' : 'A code cannot be renamed.'} error={show('code')}>
          <input style={{ ...input, textTransform: 'uppercase', letterSpacing: '0.08em' }} value={f.code} disabled={!isNew}
            onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} maxLength={20} />
        </Field>
        <Field title="Discount type">
          <div style={{ display: 'flex' }}>
            {[['flat', '₹ off'], ['percent', '% off']].map(([k, t]) => (
              <button key={k} type="button" onClick={() => setF((x) => ({ ...x, type: k }))}
                style={{ ...btn(f.type === k), flex: 1, borderRadius: 0 }}>{t}</button>
            ))}
          </div>
        </Field>
        <Field title={f.type === 'percent' ? 'Percent off' : 'Rupees off'} error={show('value')}>
          <input style={input} type="number" min="0" value={f.value} onChange={set('value')} placeholder={f.type === 'percent' ? '10' : '500'} />
        </Field>
        {f.type === 'percent' && (
          <Field title="Most it can take off (₹)" note="Empty = no cap." error={show('maxDiscount')}>
            <input style={input} type="number" min="0" value={f.maxDiscount} onChange={set('maxDiscount')} placeholder="1000" />
          </Field>
        )}
        <Field title="Minimum order (₹)" note="The bag after item discounts. Empty = any order." error={show('minOrder')}>
          <input style={input} type="number" min="0" value={f.minOrder} onChange={set('minOrder')} placeholder="4500" />
        </Field>
        <Field title="Starts" note="Empty = from now.">
          <input style={input} type="date" value={f.startsAt} onChange={set('startsAt')} />
        </Field>
        <Field title="Ends (last day)" note="Empty = no end date." error={show('endsAt')}>
          <input style={input} type="date" value={f.endsAt} onChange={set('endsAt')} />
        </Field>
        <Field title="Uses per customer" note="1 = once each. Empty = no limit.">
          <input style={input} type="number" min="0" value={f.perCustomer} onChange={set('perCustomer')} placeholder="1" />
        </Field>
        <Field title="Total uses" note="All customers together. Empty = no limit.">
          <input style={input} type="number" min="0" value={f.totalLimit} onChange={set('totalLimit')} placeholder="100" />
        </Field>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.85rem' }}>
        <input type="checkbox" checked={f.firstOrderOnly} onChange={set('firstOrderOnly')} />
        First order only <span style={hint}>(the customer must be signed in and have no earlier order)</span>
      </label>

      <details>
        <summary style={{ cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, color: '#7a6750' }}>Custom wording (optional)</summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginTop: 12 }}>
          <Field title="Headline" note={`Empty = "${couponHeadline({ ...preview, headline: '' })}"`}>
            <input style={input} value={f.headline} onChange={set('headline')} />
          </Field>
          <Field title="Terms line" note="Empty = written from the rules above.">
            <input style={input} value={f.note} onChange={set('note')} />
          </Field>
        </div>
      </details>

      <div style={{ background: '#f6f0e6', border: '1px dashed #9d7130', padding: '12px 14px', borderRadius: 3 }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.14em', color: '#9d7130', textTransform: 'uppercase' }}>The customer sees</div>
        <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ background: '#000', color: '#ddb56c', padding: '3px 8px', fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.8rem' }}>{f.code || 'CODE'}</span>
          <strong>{couponHeadline(preview)}</strong>
          <span style={{ color: '#4a4034', fontSize: '0.88rem' }}>{couponTerms(preview)}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button type="button" style={btn(false)} onClick={onCancel}>Cancel</button>
        <button type="button" style={btn(true)} onClick={save} disabled={busy}>{busy ? 'Saving…' : isNew ? 'Create coupon' : 'Save changes'}</button>
      </div>
    </div>
  );
}

function Uses({ code }) {
  const [rows, setRows] = useState(null);
  useEffect(() => db.collection(COUPONS_COLLECTION).doc(code).collection('uses').orderBy('at', 'desc').limit(30).onSnapshot(
    (snap) => { const out = []; snap.forEach((d) => out.push({ id: d.id, ...d.data() })); setRows(out); },
    () => setRows([]),
  ), [code]);
  if (!rows) return <div style={hint}>Loading…</div>;
  if (!rows.length) return <div style={hint}>Not used yet.</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
      <thead><tr style={{ textAlign: 'left', color: '#7a6750' }}><th>When</th><th>Order</th><th>Customer</th><th style={{ textAlign: 'right' }}>Bag</th><th style={{ textAlign: 'right' }}>Off</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} style={{ borderTop: '1px dashed #e2d7c6' }}>
            <td style={{ padding: '6px 4px' }}>{when(r.at)}</td><td>{r.orderId}</td><td>{r.account || 'guest'}</td>
            <td style={{ textAlign: 'right' }}>₹{Number(r.subtotal || 0).toLocaleString('en-IN')}</td>
            <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{Number(r.discount || 0).toLocaleString('en-IN')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CouponsPage() {
  const [coupons, setCoupons] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null); // { isNew, initial }
  const [openUses, setOpenUses] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => db.collection(COUPONS_COLLECTION).onSnapshot(
    (snap) => { const out = []; snap.forEach((d) => out.push(normalizeCoupon(d.data(), d.id))); setCoupons(out); },
    (e) => { console.error('coupons', e); setCoupons([]); setMessage('Could not read coupons. Check that the Firestore rules allow the Coupons collection.'); },
  ), []);

  const now = Date.now();
  const list = useMemo(() => (coupons || []).map((c) => ({ ...c, state: couponState(c, now) }))
    .sort((a, b) => (a.state === 'running' ? -1 : 0) - (b.state === 'running' ? -1 : 0) || a.code.localeCompare(b.code)), [coupons, now]);
  const shown = list.filter((c) => filter === 'all' || (filter === 'done' ? ['ended', 'used_up'].includes(c.state) : c.state === filter));
  const count = (st) => list.filter((c) => (Array.isArray(st) ? st.includes(c.state) : c.state === st)).length;
  const taken = new Set(list.map((c) => c.code));

  const flash = (t) => { setMessage(t); setTimeout(() => setMessage(''), 3500); };
  // A write Firestore refuses must say so, not leave the button doing nothing.
  const failed = (what, e) => {
    console.error(what, e);
    const denied = String(e?.code || e?.message || '').includes('permission');
    setMessage(denied
      ? `Could not ${what}: the Firestore rules do not allow the Coupons collection yet.`
      : `Could not ${what}. Please try again. (${e?.message || e})`);
  };
  const save = async (doc, isNew) => {
    const ref = db.collection(COUPONS_COLLECTION).doc(doc.code);
    try {
      if (isNew) await ref.set({ ...doc, usedCount: 0, createdAt: new Date(), updatedAt: new Date() });
      else await ref.set({ ...doc, updatedAt: new Date() }, { merge: true });
    } catch (e) {
      failed(`save ${doc.code}`, e);
      return;
    }
    setEditing(null);
    flash(`${doc.code} saved. It is live on the website now.`);
  };
  const toggle = async (c) => {
    try {
      await db.collection(COUPONS_COLLECTION).doc(c.code).set({ active: !c.active, updatedAt: new Date() }, { merge: true });
      flash(`${c.code} ${c.active ? 'paused' : 'turned on'}.`);
    } catch (e) { failed(`change ${c.code}`, e); }
  };
  const remove = async (c) => {
    const ask = c.usedCount
      ? `${c.code} has been used ${c.usedCount} times. Delete it anyway? (Pausing keeps its history.)`
      : `Delete ${c.code}?`;
    if (!window.confirm(ask)) return;
    try {
      await db.collection(COUPONS_COLLECTION).doc(c.code).delete();
      flash(`${c.code} deleted.`);
    } catch (e) { failed(`delete ${c.code}`, e); }
  };
  const seed = async () => {
    const batch = db.batch();
    Object.values(COUPONS).forEach((c) => {
      batch.set(db.collection(COUPONS_COLLECTION).doc(c.code), { ...docFrom(formFrom(normalizeCoupon(c))), usedCount: 0, createdAt: new Date(), updatedAt: new Date() });
    });
    try {
      await batch.commit();
      flash('The current offers are here now. Edit them like any other coupon.');
    } catch (e) { failed('bring in the current offers', e); }
  };

  if (coupons === null) return <div style={{ padding: 20 }}>Loading coupons…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0 40px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
        {[['Running', count('running'), '#187044'], ['Scheduled', count('scheduled'), '#1d4f91'], ['Ended / used up', count(['ended', 'used_up']), '#7a6750'], ['Off', count('off'), '#9b2c2c'],
          ['Total uses', list.reduce((s, c) => s + c.usedCount, 0), '#3e4a2f']].map(([t, v, color]) => (
          <div key={t} style={{ ...card, padding: '11px 12px' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 900, color }}>{v}</div>
            <div style={{ marginTop: 4, fontSize: '0.64rem', color: '#6A7079', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</div>
          </div>
        ))}
      </div>

      {message && <div style={{ ...card, background: '#f6f0e6', borderColor: '#9d7130', fontWeight: 600, fontSize: '0.88rem' }}>{message}</div>}

      {editing ? (
        <CouponForm
          key={editing.initial.code || 'new'}
          initial={editing.initial}
          isNew={editing.isNew}
          taken={taken}
          onCancel={() => setEditing(null)}
          onSave={(doc) => save(doc, editing.isNew)}
        />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map(([k, t]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} style={{ ...btn(filter === k), height: 32 }}>{t}</button>
            ))}
          </div>
          <button type="button" style={btn(true)} onClick={() => setEditing({ isNew: true, initial: blank() })}>+ New coupon</button>
        </div>
      )}

      {list.length === 0 && !editing && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          <strong>No coupons here yet.</strong>
          <span style={{ fontSize: '0.88rem', color: '#4a4034' }}>
            Until you save one, the website offers the built-in codes: {Object.values(COUPONS).map((c) => `${c.code} (${couponTerms(normalizeCoupon(c)).replace(/\.$/, '')})`).join(' and ')}.
            Bring them here to edit, pause or limit them. As soon as any coupon is saved here, only the coupons on this page work.
          </span>
          <button type="button" style={btn(true)} onClick={seed}>Bring in the current offers</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((c) => {
          const st = STATE[c.state];
          return (
            <div key={c.code} style={{ ...card, opacity: c.state === 'running' || c.state === 'scheduled' ? 1 : 0.8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 14, alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ background: '#000', color: '#ddb56c', padding: '4px 9px', fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.85rem' }}>{c.code}</span>
                    <strong style={{ fontSize: '1rem' }}>{couponHeadline(c)}</strong>
                    <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 3, fontSize: '0.7rem', fontWeight: 800 }}>{st.label}</span>
                    {c.firstOrderOnly && <span style={{ ...hint, fontWeight: 700 }}>· First order</span>}
                  </div>
                  <span style={{ fontSize: '0.85rem', color: '#4a4034' }}>{couponTerms(c)}</span>
                  <span style={hint}>
                    Used {c.usedCount}{c.totalLimit ? ` of ${c.totalLimit}` : ''} times
                    {c.startsAt ? ` · from ${new Date(c.startsAt).toLocaleDateString('en-IN')}` : ''}
                    {c.endsAt ? ` · until ${new Date(c.endsAt).toLocaleDateString('en-IN')}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button type="button" style={btn(false)} onClick={() => setOpenUses(openUses === c.code ? '' : c.code)}>{openUses === c.code ? 'Hide uses' : 'View uses'}</button>
                  <button type="button" style={btn(false)} onClick={() => { setEditing({ isNew: false, initial: formFrom(c) }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Edit</button>
                  <button type="button" style={btn(false)} onClick={() => toggle(c)}>{c.active ? 'Pause' : 'Turn on'}</button>
                  <button type="button" style={{ ...btn(false), color: '#b33b2f' }} onClick={() => remove(c)}>Delete</button>
                </div>
              </div>
              {openUses === c.code && <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2d7c6' }}><Uses code={c.code} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CouponsPage;
